// Persistent chrome that wraps every authenticated route. Two-row header per
// .impeccable/shapes/dashboard-shell.md: row 1 is identity + switchers + status
// + user menu; row 2 is section navigation. Page content fills the remainder.

import { useAuthState } from 'react-firebase-hooks/auth'
import { Link, Outlet, useParams } from 'react-router-dom'
import { useProjectAttentionCount, useTeam, useTeamAttentionCount } from '../../lib/data-hooks.js'
import { auth } from '../../lib/firebase.js'
import { FeedbackButton } from './FeedbackButton.jsx'
import { ProjectSwitcher } from './ProjectSwitcher.jsx'
import { SectionTabs } from './SectionTabs.jsx'
import { StatusIcon } from './StatusIcon.jsx'
import { TeamSwitcher } from './TeamSwitcher.jsx'
import { UserMenu } from './UserMenu.jsx'

export function DashboardShell() {
  const { teamId, projectId } = useParams()
  const [user] = useAuthState(auth)
  const teamAttention = useTeamAttentionCount(teamId)
  const projectAttention = useProjectAttentionCount(teamId, projectId)
  const totalAttention = teamId ? teamAttention : 0
  const { data: team } = useTeam(teamId)
  const isBeta = Boolean(team?.is_beta)

  async function getToken() {
    if (!user) throw new Error('Not signed in')
    return await user.getIdToken()
  }

  return (
    <div className="flex min-h-screen flex-col bg-[var(--color-limestone)]">
      <header className="sticky top-0 z-30 border-b border-[var(--color-sandstone)] bg-[var(--color-limestone)]/95 backdrop-blur">
        <div className="flex h-12 items-center gap-3 px-4">
          <Link
            to="/teams"
            className="font-medium tracking-tight text-[var(--color-terracotta)]"
            aria-label="Cairn — back to teams"
          >
            cairn
          </Link>

          <div className="ml-2 flex items-center gap-1">
            <TeamSwitcher teamId={teamId} attentionCount={teamAttention} />
            {teamId && (
              <>
                <span aria-hidden="true" className="text-[var(--color-schist)]">
                  /
                </span>
                <ProjectSwitcher
                  teamId={teamId}
                  projectId={projectId}
                  attentionCount={projectAttention}
                />
              </>
            )}
          </div>

          <div className="ml-auto flex items-center gap-2">
            {isBeta && (
              <span className="rounded-sm bg-[var(--color-terracotta)]/15 px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wider text-[var(--color-terracotta-deep)]">
                Beta
              </span>
            )}
            {isBeta && <FeedbackButton getToken={getToken} />}
            <StatusIcon attentionCount={totalAttention} />
            <UserMenu />
          </div>
        </div>
        <SectionTabs teamId={teamId} projectId={projectId} />
      </header>

      <main id="main-content" className="flex-1">
        <Outlet />
      </main>

      <footer className="border-t border-[var(--color-sandstone)] px-6 py-4 text-xs text-[var(--color-schist)]">
        <Link to="/docs" className="hover:text-[var(--color-terracotta)]">
          Docs
        </Link>
        <span className="mx-2" aria-hidden="true">
          ·
        </span>
        <a
          href="https://github.com/cairntools/cairn"
          className="hover:text-[var(--color-terracotta)]"
        >
          GitHub
        </a>
        <span className="mx-2" aria-hidden="true">
          ·
        </span>
        <span>v0.0.0-alpha</span>
      </footer>
    </div>
  )
}
